import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useStores } from "@/lib/stores";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, UserPlus, Trash2, ShieldCheck, Users, Tag, Lock, Plus } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function AdminSettings() {
  const { user } = useAuth();
  const { stores } = useStores();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);

  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "", role: "admin", store_id: "" });
  const [creating, setCreating] = useState(false);

  const [resetTarget, setResetTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  // Tags system
  const [tags, setTags] = useState([]);
  const [tagPickerUser, setTagPickerUser] = useState(null);
  const [ceoPassword, setCeoPassword] = useState("");
  const [newTag, setNewTag] = useState({ name: "", permissions: "" });
  const isSuper = user?.role === "superadmin";
  const isCEO = isSuper && (user?.tags || []).includes("ceo");

  const loadTags = async () => {
    try { const { data } = await api.get("/admin/tags"); setTags(data); }
    catch (e) { logError("[tags] load:", e); }
  };
  useEffect(() => { loadTags(); }, []);

  const openTagPicker = (u) => { setTagPickerUser(u); setCeoPassword(""); };
  const closeTagPicker = () => { setTagPickerUser(null); setCeoPassword(""); };

  const assignTag = async (slug) => {
    if (!tagPickerUser) return;
    if ((tagPickerUser.tags || []).includes(slug)) { toast.info("Usuário já possui essa tag"); return; }
    const payload = { tag_slug: slug };
    if (slug === "ceo") payload.ceo_password = ceoPassword;
    try {
      await api.post(`/admin/users/${tagPickerUser.user_id}/tags`, payload);
      toast.success(`Tag "${slug}" atribuída`);
      await loadUsers();
      setTagPickerUser((u) => u ? { ...u, tags: [...(u.tags || []), slug] } : null);
      setCeoPassword("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const removeTagFromUser = async (slug) => {
    if (!tagPickerUser) return;
    let ceoPwd = "";
    if (slug === "ceo") {
      ceoPwd = window.prompt("Digite a senha de permissão CEO para remover essa tag:") || "";
      if (!ceoPwd) return;
    }
    try {
      const url = slug === "ceo"
        ? `/admin/users/${tagPickerUser.user_id}/tags/${slug}?ceo_password=${encodeURIComponent(ceoPwd)}`
        : `/admin/users/${tagPickerUser.user_id}/tags/${slug}`;
      await api.delete(url);
      toast.success(`Tag "${slug}" removida`);
      await loadUsers();
      setTagPickerUser((u) => u ? { ...u, tags: (u.tags || []).filter((t) => t !== slug) } : null);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const createTag = async (e) => {
    e.preventDefault();
    if (!newTag.name.trim()) return toast.error("Nome obrigatório");
    const perms = newTag.permissions.split(",").map((p) => p.trim()).filter(Boolean);
    try {
      await api.post("/admin/tags", { name: newTag.name.trim(), permissions: perms });
      toast.success("Tag criada");
      setNewTag({ name: "", permissions: "" });
      loadTags();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const deleteTag = async (tag) => {
    if (!window.confirm(`Excluir a tag "${tag.name}"? Todos os usuários perderão esta tag.`)) return;
    try {
      await api.delete(`/admin/tags/${tag.tag_id}`);
      toast.success("Tag excluída");
      loadTags(); loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const loadUsers = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadUsers(); }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password.length < 6) return toast.error("Nova senha muito curta (mín 6)");
    if (pwd.new_password !== pwd.confirm) return toast.error("Confirmação não bate");
    setPwdLoading(true);
    try {
      await api.post("/admin/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Senha alterada.");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setPwdLoading(false); }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.email || !newAdmin.password) return toast.error("Preencha e-mail e senha");
    if (newAdmin.role === "admin" && !newAdmin.store_id) return toast.error("Selecione a loja do admin");
    setCreating(true);
    try {
      await api.post("/admin/users", { ...newAdmin, store_id: newAdmin.role === "admin" ? newAdmin.store_id : null });
      toast.success(`${newAdmin.role === "superadmin" ? "Super-admin" : "Admin"} criado.`);
      setNewAdmin({ name: "", email: "", password: "", role: "admin", store_id: "" });
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setCreating(false); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Excluir ${u.email}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("Usuário excluído.");
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const resetPassword = async () => {
    if (newPwd.length < 6) return toast.error("Senha muito curta (mín 6)");
    try {
      await api.post(`/admin/users/${resetTarget.user_id}/reset-password`, { new_password: newPwd });
      toast.success(`Senha de ${resetTarget.email} redefinida.`);
      setResetTarget(null); setNewPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl">
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-neutral-500 mb-3">Sistema</div>
      <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Configurações</h1>
      <p className="mt-2 text-sm text-neutral-600">Gerencie sua senha e os administradores da plataforma.</p>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        {/* Change own password */}
        <form onSubmit={changePassword} className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="change-password-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-neutral-900 text-white rounded-lg grid place-items-center"><KeyRound size={16}/></div>
            <div>
              <div className="font-heading font-bold text-lg">Alterar minha senha</div>
              <div className="text-xs text-neutral-500">{user?.email}</div>
            </div>
          </div>
          <div className="space-y-3">
            <div><Label>Senha atual</Label><PasswordInput value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="mt-1.5 h-11" data-testid="pwd-current" required /></div>
            <div><Label>Nova senha</Label><PasswordInput value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="mt-1.5 h-11" minLength={6} data-testid="pwd-new" required /></div>
            <div><Label>Confirmar</Label><PasswordInput value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="mt-1.5 h-11" minLength={6} data-testid="pwd-confirm" required /></div>
          </div>
          <Button type="submit" disabled={pwdLoading} className="w-full mt-5 h-11 bg-neutral-900 hover:bg-neutral-800" data-testid="pwd-submit">
            {pwdLoading ? "Salvando…" : "Atualizar senha"}
          </Button>
        </form>

        {/* Add new admin */}
        <form onSubmit={createAdmin} className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="create-admin-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-neutral-900 text-white rounded-lg grid place-items-center"><UserPlus size={16}/></div>
            <div>
              <div className="font-heading font-bold text-lg">Novo administrador</div>
              <div className="text-xs text-neutral-500">Escolha as autorizações abaixo</div>
            </div>
          </div>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} className="mt-1.5 h-11" data-testid="na-name" required /></div>
            <div><Label>E-mail (login)</Label><Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} className="mt-1.5 h-11" data-testid="na-email" required /></div>
            <div><Label>Senha inicial</Label><PasswordInput value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} className="mt-1.5 h-11" minLength={6} data-testid="na-password" required /></div>
            <div>
              <Label>Autorização</Label>
              <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="na-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin de loja (acesso apenas à sua loja)</SelectItem>
                  <SelectItem value="superadmin">Super-admin (acesso total a todas as lojas)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newAdmin.role === "admin" && (
              <div>
                <Label>Loja</Label>
                <Select value={newAdmin.store_id} onValueChange={(v) => setNewAdmin({ ...newAdmin, store_id: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="na-store"><SelectValue placeholder="Selecione a loja"/></SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => <SelectItem key={s.store_id} value={s.store_id}>{s.name} · {s.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <Button type="submit" disabled={creating} className="w-full mt-5 h-11 bg-neutral-900 hover:bg-neutral-800" data-testid="na-submit">
            {creating ? "Criando…" : "Criar administrador"}
          </Button>
        </form>
      </div>

      {/* Admin list */}
      <div className="mt-10 bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-neutral-100 flex items-center gap-2">
          <Users size={14} className="text-neutral-500"/>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Administradores ({users.length})</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Nome</th>
              <th className="px-4 py-3 text-left font-semibold">E-mail</th>
              <th className="px-4 py-3 text-left font-semibold">Autorização</th>
              <th className="px-4 py-3 text-left font-semibold">Loja</th>
              <th className="px-4 py-3 text-right font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading && <tr><td colSpan={5} className="p-6 text-center text-neutral-500">Carregando…</td></tr>}
            {!loading && users.map((u) => (
              <tr key={u.user_id} className="hover:bg-neutral-50" data-testid={`admin-row-${u.email}`}>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3 text-neutral-700">{u.email}</td>
                <td className="px-4 py-3">
                  {u.role === "superadmin"
                    ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-neutral-900 text-white px-2 py-1 rounded"><ShieldCheck size={10}/> Superadmin</span>
                    : <span className="text-[10px] uppercase tracking-widest font-bold bg-neutral-100 text-neutral-700 px-2 py-1 rounded">Admin</span>}
                  {(u.tags || []).length > 0 && (
                    <span className="ml-1 inline-flex gap-1">
                      {u.tags.map((t) => (
                        <span key={t} data-testid={`user-row-tag-${u.email}-${t}`} className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${t === "ceo" ? "bg-amber-500 text-white" : "bg-neutral-200 text-neutral-700"}`}>{t}</span>
                      ))}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-neutral-600">{u.store_name || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    {isSuper && <Button size="sm" variant="outline" onClick={() => openTagPicker(u)} data-testid={`assign-tag-${u.email}`}><Tag size={12} className="mr-1"/> Tags</Button>}
                    <Button size="sm" variant="outline" onClick={() => { setResetTarget(u); setNewPwd(""); }} data-testid={`reset-pwd-${u.email}`}>
                      <KeyRound size={12} className="mr-1"/> Nova senha
                    </Button>
                    {u.user_id !== user?.user_id && u.email !== "admin@tecassist.com" && (
                      <Button size="sm" variant="outline" onClick={() => deleteUser(u)} className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50" data-testid={`delete-user-${u.email}`}>
                        <Trash2 size={12}/>
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Redefinir senha — {resetTarget?.email}</DialogTitle></DialogHeader>
          <div>
            <Label>Nova senha (mín 6 caracteres)</Label>
            <PasswordInput value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1.5 h-11" minLength={6} data-testid="reset-new-pwd" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>Cancelar</Button>
            <Button onClick={resetPassword} className="bg-neutral-900 hover:bg-neutral-800" data-testid="reset-confirm">Redefinir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TAGS SECTION */}
      {isSuper && (
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <Tag size={16} className="text-neutral-500"/>
            <h2 className="font-heading font-black text-2xl">Tags de permissão</h2>
          </div>
          <p className="text-sm text-neutral-600 mb-6">Tags dão poderes especiais aos usuários. Somente o CEO pode criar/editar tags. Superadmins podem atribuir tags a outros usuários.</p>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-neutral-200 rounded-xl p-6">
              <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-3">Tags existentes ({tags.length})</div>
              <div className="space-y-2">
                {tags.map((t) => (
                  <div key={t.tag_id} className="flex items-start justify-between gap-3 p-3 border border-neutral-100 rounded-lg" data-testid={`tag-item-${t.slug}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded ${t.slug === "ceo" ? "bg-amber-500 text-white" : "bg-neutral-200 text-neutral-700"}`}>{t.slug}</span>
                        <span className="font-semibold text-sm">{t.name}</span>
                        {t.protected && <Lock size={11} className="text-neutral-400" title="Protegida"/>}
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-1">Permissões: {(t.permissions || []).join(", ") || <i>nenhuma extra</i>}</div>
                    </div>
                    {isCEO && !t.protected && (
                      <button onClick={() => deleteTag(t)} className="text-neutral-400 hover:text-red-500 p-1" data-testid={`del-tag-${t.slug}`}><Trash2 size={14}/></button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {isCEO ? (
              <form onSubmit={createTag} className="bg-white border border-neutral-200 rounded-xl p-6" data-testid="create-tag-form">
                <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-3">Criar nova tag (CEO)</div>
                <div className="space-y-3">
                  <div><Label>Nome</Label><Input value={newTag.name} onChange={(e) => setNewTag({ ...newTag, name: e.target.value })} className="mt-1.5 h-11" placeholder="Ex: Gerente Financeiro" data-testid="new-tag-name" required /></div>
                  <div>
                    <Label>Permissões (separadas por vírgula)</Label>
                    <Input value={newTag.permissions} onChange={(e) => setNewTag({ ...newTag, permissions: e.target.value })} className="mt-1.5 h-11 font-mono text-sm" placeholder="ex: view_finance,export_reports" data-testid="new-tag-perms" />
                    <p className="text-[11px] text-neutral-500 mt-1">Referência: as permissões são strings livres que você pode usar em futuras regras (ex: view_finance, export_reports, edit_prices).</p>
                  </div>
                </div>
                <Button type="submit" className="w-full mt-5 h-11 bg-amber-500 hover:bg-amber-600 text-white" data-testid="create-tag-submit"><Plus size={14} className="mr-1.5"/> Criar tag</Button>
              </form>
            ) : (
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-6 text-sm text-neutral-600 flex items-start gap-3">
                <Lock size={16} className="mt-0.5 shrink-0 text-neutral-400"/>
                <div>Apenas usuários com a tag <b>CEO</b> podem criar novas tags ou alterar permissões. Peça ao CEO para conceder essa permissão.</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAG PICKER DIALOG */}
      <Dialog open={!!tagPickerUser} onOpenChange={(v) => !v && closeTagPicker()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Tags de {tagPickerUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest font-bold text-neutral-500">Tags atuais</div>
            <div className="flex flex-wrap gap-2">
              {(tagPickerUser?.tags || []).length === 0 && <span className="text-xs text-neutral-500">Nenhuma tag ainda</span>}
              {(tagPickerUser?.tags || []).map((slug) => (
                <span key={slug} className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold px-2 py-1 rounded ${slug === "ceo" ? "bg-amber-500 text-white" : "bg-neutral-200 text-neutral-700"}`} data-testid={`user-tag-${slug}`}>
                  {slug}
                  <button onClick={() => removeTagFromUser(slug)} className="opacity-70 hover:opacity-100" aria-label={`Remover ${slug}`}><Trash2 size={10}/></button>
                </span>
              ))}
            </div>
            <div className="border-t border-neutral-100 pt-4">
              <div className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-2">Atribuir tag</div>
              <div className="grid grid-cols-2 gap-2">
                {tags.filter((t) => !(tagPickerUser?.tags || []).includes(t.slug)).map((t) => (
                  <div key={t.slug} className="border border-neutral-200 rounded-lg p-2">
                    <div className="text-sm font-semibold flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${t.slug === "ceo" ? "bg-amber-500 text-white" : "bg-neutral-200 text-neutral-700"}`}>{t.slug}</span>
                      {t.name}
                    </div>
                    {t.slug === "ceo" ? (
                      <div className="mt-2 space-y-2">
                        <PasswordInput placeholder="Senha de permissão CEO" value={ceoPassword} onChange={(e) => setCeoPassword(e.target.value)} className="h-9" data-testid="ceo-password-input" />
                        <Button size="sm" onClick={() => assignTag(t.slug)} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid={`assign-ceo-btn`}>Confirmar CEO</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => assignTag(t.slug)} className="w-full mt-2" data-testid={`assign-tag-${t.slug}`}>Atribuir</Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={closeTagPicker}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
